import React from "react";

import { motion } from "framer-motion";

import { ArrowRight, Star } from "lucide-react";

import { useNavigate } from "react-router-dom";

import AnimatedHeading from "../../animations/AnimatedHeading";

const CTA = () => {
  const navigate = useNavigate();

  return (
    <section
      className="
        relative
        overflow-hidden
        theme-bg-primary
        pb-12
        font-saira
      "
    >
      {/* ============================================================
          BACKGROUND GLOW
      ============================================================ */}
      <div className="absolute inset-0">
        <motion.div
          animate={{
            scale: [1, 1.12, 1],
            opacity: [0.22, 0.4, 0.22],
          }}
          transition={{
            duration: 6,
            repeat: Infinity,
          }}
          className="
            absolute
            left-1/2
            top-1/2
            h-[280px]
            w-[280px]
            sm:h-[340px]
            sm:w-[340px]
            md:h-[400px]
            md:w-[400px]
            -translate-x-1/2
            -translate-y-1/2
            rounded-full
            bg-[var(--brand-primary)]
            blur-[120px]
          "
        />
      </div>

      {/* ============================================================
          FLOATING PARTICLES
      ============================================================ */}
      {[...Array(8)].map((_, i) => (
        <motion.span
          key={i}
          className="
            absolute
            rounded-full
            bg-[var(--brand-primary)]
          "
          style={{
            width: `${4 + Math.random() * 5}px`,
            height: `${4 + Math.random() * 5}px`,
            left: `${10 + Math.random() * 80}%`,
            top: `${10 + Math.random() * 80}%`,
            opacity: 0.25,
          }}
          animate={{
            y: [0, -25, 0],
            opacity: [0.2, 0.7, 0.2],
            scale: [1, 1.4, 1],
          }}
          transition={{
            duration: 3 + Math.random() * 3,
            repeat: Infinity,
            delay: Math.random() * 2,
          }}
        />
      ))}

      {/* ============================================================
          CONTENT
      ============================================================ */}
      <div
        className="
          relative
          z-10
          mx-auto
          max-w-6xl
          px-4
          sm:px-6
        "
      >
        <motion.div
          initial={{
            opacity: 0,
            y: 60,
          }}
          whileInView={{
            opacity: 1,
            y: 0,
          }}
          viewport={{
            once: true,
          }}
          transition={{
            duration: 0.8,
            ease: "easeOut",
          }}
        >
          {/* ========================================================
              CTA CARD
          ======================================================== */}
          <div
            className="
              theme-card
              rounded-3xl
              border
              theme-border
              backdrop-blur-xl
              shadow-[0_20px_70px_var(--shadow-color)]
            "
          >
            <div
              className="
                px-5
                py-10
                text-center
                sm:px-8
                sm:py-12
                md:px-12
                md:py-14
                lg:px-16
                lg:py-16
              "
            >
              {/* ====================================================
                  ICON
              ==================================================== */}
              <motion.div
                initial={{
                  opacity: 0,
                  scale: 0.5,
                  rotate: -180,
                }}
                whileInView={{
                  opacity: 1,
                  scale: 1,
                  rotate: 0,
                }}
                viewport={{
                  once: true,
                }}
                transition={{
                  duration: 0.8,
                  type: "spring",
                  stiffness: 120,
                }}
                className="
                  relative
                  mx-auto
                  mb-6
                  w-fit
                "
              >
                {/* Icon Glow */}
                <motion.div
                  animate={{
                    scale: [1, 1.25, 1],
                    opacity: [0.3, 0.65, 0.3],
                  }}
                  transition={{
                    duration: 2.5,
                    repeat: Infinity,
                  }}
                  className="
                    absolute
                    inset-0
                    rounded-full
                    bg-[var(--brand-primary)]
                    blur-2xl
                  "
                />

                {/* Icon Circle */}
                <motion.div
                  animate={{
                    y: [0, -6, 0],
                  }}
                  whileHover={{
                    scale: 1.12,
                    rotate: 12,
                  }}
                  transition={{
                    y: {
                      duration: 3,
                      repeat: Infinity,
                      ease: "easeInOut",
                    },
                  }}
                  className="
                    relative
                    flex
                    h-16
                    w-16
                    items-center
                    justify-center
                    rounded-full
                    bg-[var(--brand-primary)]
                    shadow-[0_0_40px_rgba(249,115,22,0.35)]
                    sm:h-[72px]
                    sm:w-[72px]
                    md:h-20
                    md:w-20
                  "
                >
                  <Star
                    size={30}
                    className="text-[var(--text-primary)] md:h-[34px] md:w-[34px]"
                    strokeWidth={2.5}
                  />
                </motion.div>
              </motion.div>

              {/* ====================================================
                  LABEL
              ==================================================== */}
              <p
                className="
                  text-[10px]
                  font-bold
                  uppercase
                  tracking-[0.3em]
                  brand-color
                  sm:text-xs
                  md:text-sm
                "
              >
                [ JOIN OUR TEAM ]
              </p>

              {/* ====================================================
                  HEADING
              ==================================================== */}
              <AnimatedHeading
                as="h2"
                parts={["Ready to Start Your Journey?"]}
                className="
                  mt-5
                  text-2xl
                  font-semibold
                  leading-tight
                  tracking-tight
                  theme-text-primary
                  sm:text-3xl
                  md:text-4xl
                  lg:text-5xl
                  xl:text-6xl
                "
              />

              {/* ====================================================
                  DESCRIPTION
              ==================================================== */}
              <motion.p
                initial={{
                  opacity: 0,
                }}
                whileInView={{
                  opacity: 1,
                }}
                transition={{
                  delay: 0.3,
                }}
                viewport={{
                  once: true,
                }}
                className="
                  theme-text-muted
                  mx-auto
                  mt-5
                  max-w-2xl
                  text-sm
                  leading-7
                  sm:text-base
                  md:text-lg
                  lg:max-w-3xl
                "
              >
                Join WebBocket Software and build innovative digital
                products with a passionate team creating impactful
                technology solutions.
              </motion.p>

              {/* ====================================================
                  BUTTON
              ==================================================== */}
              <motion.div
                initial={{
                  opacity: 0,
                  y: 25,
                }}
                whileInView={{
                  opacity: 1,
                  y: 0,
                }}
                viewport={{
                  once: true,
                }}
                transition={{
                  delay: 0.4,
                  duration: 0.7,
                }}
                className="
                  mt-8
                  flex
                  justify-center
                "
              >
                <motion.button
                  whileHover={{
                    scale: 1.05,
                    y: -3,
                  }}
                  whileTap={{
                    scale: 0.96,
                  }}
                  transition={{
                    type: "spring",
                    stiffness: 250,
                  }}
                  onClick={() => {
                    navigate("/contact#contact-form");
                  }}
                  className="
                    group
                    inline-flex
                    items-center
                    gap-3
                    rounded-xl
                    bg-[var(--brand-primary)]
                    px-6
                    py-3.5
                    text-sm
                    font-semibold
                    text-white
                    shadow-[0_10px_30px_rgba(249,115,22,0.25)]
                    transition-all
                    duration-300
                    hover:bg-[var(--brand-hover)]
                    sm:px-8
                    sm:py-4
                    sm:text-base
                  "
                >
                  Contact Us

                  <motion.div
                    animate={{
                      x: [0, 5, 0],
                    }}
                    transition={{
                      duration: 1.2,
                      repeat: Infinity,
                    }}
                  >
                    <ArrowRight size={18} />
                  </motion.div>
                </motion.button>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default CTA;